/* =============================================================
   Account settings on /account: your plan and what it allows, the
   weekly newsletter, your profile (display name, password) and your
   data (download it, or delete the account and everything in it).
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useAuth, startPasswordChange } from '../lib/auth';
import { deleteMyAccount, downloadMyData, useNewsletter, usePlan, useSetNewsletter, useUpdateName } from '../lib/account';
import { signOutEverywhere } from '../lib/msal';

const head = 'font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-4';
const btn = 'py-2 px-4 border border-ink text-ink text-sm font-label disabled:opacity-40';
const note = 'font-editorial italic text-[14px] text-ink-muted';
const errCls = 'font-editorial italic text-[14px] text-[#a02525] mt-2';
const msg = (e: unknown) => (e instanceof Error ? e.message : 'That did not work. Try again.');

function Meter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const full = used >= limit;
  return (
    <div>
      <div className="flex justify-between font-editorial text-[15px] text-ink mb-1">
        <span>{label}</span>
        <span className={`tabular-nums ${full ? 'text-[#a02525]' : 'text-ink-soft'}`}>
          {used} of {limit}
        </span>
      </div>
      <div className="h-1.5 bg-paper-deep border border-rule overflow-hidden" aria-hidden="true">
        <div className="h-full" style={{ width: `${pct}%`, background: full ? '#a02525' : 'var(--accent, #8a6d3b)' }} />
      </div>
    </div>
  );
}

export function PlanSection() {
  const { user } = useAuth();
  const q = usePlan(user?.id);
  if (q.error)
    return (
      <section className="mb-12">
        <h2 className={head}>Your plan</h2>
        <p className={errCls}>Your plan couldn't be loaded ({msg(q.error)}). Refresh the page to try again.</p>
      </section>
    );
  if (!q.data) return null;
  const p = q.data;
  return (
    <section className="mb-12">
      <h2 className={head}>Your plan · {p.plan === 'paid' ? 'Paid Member' : 'Member'}</h2>
      {p.plan === 'paid' ? (
        <p className="font-editorial text-[15px] text-ink-soft">No limits on maps, checklists or invitations. You can also read every Paid Members' piece in the Resource hub, publish maps, and download checklists as PDF or Excel.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-4">
            <Meter label="Interaction Maps" used={p.maps} limit={p.limits.maps} />
            <Meter label="CYBHI checklists" used={p.checklists} limit={p.limits.checklists} />
            <Meter label="People invited" used={p.invites} limit={p.limits.invites} />
          </div>
          <p className={note}>
            Deleting a map or checklist, or withdrawing an invitation, frees a slot. A Paid Member plan is coming: no limits, plus every Paid Members' piece in the Resource hub, publishing maps, and downloading checklists as PDF or Excel.
          </p>
        </>
      )}
    </section>
  );
}

/** The weekly newsletter: any member, free or paid, can sign up or stop it here (#newsletter is linked from every issue). */
export function NewsletterSection() {
  const { user } = useAuth();
  const q = useNewsletter(user?.id);
  const set = useSetNewsletter(user?.id);
  const ref = useRef<HTMLElement>(null);
  const [settling, setSettling] = useState(false);
  const loaded = !!q.data || !!q.error;
  /* Links to /account#newsletter (every issue, the notes page) land here: the browser's own jump happens
     before this page has drawn the section, so scroll once it and everything above it (plan, apps, work)
     have loaded and stopped moving it down. */
  const fetching = useIsFetching();
  const scrolled = useRef(false);
  /* The reader taking over (scrolling, a key, a click or a tap) at any point, from the first moment, stops the
     jump and the hold: their own place wins. */
  const moved = useRef(false);
  const release = useRef<() => void>(() => {});
  useEffect(() => {
    if (window.location.hash !== '#newsletter') return;
    const events = ['wheel', 'touchmove', 'keydown', 'mousedown'] as const;
    const stop = () => {
      moved.current = true;
    };
    events.forEach((t) => window.addEventListener(t, stop, { passive: true }));
    return () => {
      events.forEach((t) => window.removeEventListener(t, stop));
      release.current();
    };
  }, []);
  useEffect(() => {
    if (scrolled.current || moved.current || !loaded || fetching > 0 || window.location.hash !== '#newsletter') return;
    const el = ref.current;
    if (!el) return;
    scrolled.current = true;
    el.scrollIntoView();
    /* Blocks above can still grow for a moment (their data can land a render later, and fonts load): keep the
       section in place for up to 2 seconds, unless the reader takes over. */
    const ro = new ResizeObserver(() => {
      if (!moved.current) el.scrollIntoView();
    });
    ro.observe(document.body);
    const timer = window.setTimeout(() => ro.disconnect(), 2000);
    release.current = () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [loaded, fetching]);
  if (!user) return null;
  const on = !!q.data?.subscribed;
  /* One button switches between signing up and stopping, so a double click mustn't undo the first click. */
  const toggle = () =>
    set.mutate(!on, {
      onSuccess: () => {
        setSettling(true);
        window.setTimeout(() => setSettling(false), 800);
      },
    });
  return (
    <section ref={ref} className="mb-12 scroll-mt-24" id="newsletter">
      <h2 className={head}>Weekly newsletter{q.data ? ` · ${on ? 'signed up' : 'not signed up'}` : ''}</h2>
      <p className="font-editorial text-[15px] text-ink-soft mb-4 max-w-2xl">
        Everything new in the Resource hub (articles, essays, deep dives, masterclasses and more), once a week, sent to {user.email}. Free for every member.
      </p>
      {q.error ? (
        <p role="alert" className={errCls}>Your newsletter setting couldn't be loaded ({msg(q.error)}). Refresh the page to try again.</p>
      ) : q.data ? (
        <div className="flex flex-wrap items-center gap-4">
          <button type="button" className={btn} disabled={set.isPending || settling} onClick={toggle}>
            {set.isPending ? 'Saving…' : on ? 'Stop the newsletter' : 'Sign me up'}
          </button>
          <span className={note}>
            {on
              ? `Signed up${q.data.since ? ` on ${new Date(q.data.since).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}. Stop it here at any time, or reply “unsubscribe” to any issue.`
              : 'You can stop it at any time, here or by replying “unsubscribe” to any issue.'}
          </span>
        </div>
      ) : (
        <p className={note}>Loading…</p>
      )}
      {set.error && <p role="alert" className={errCls}>{msg(set.error)}</p>}
    </section>
  );
}

export function ProfileSection() {
  const { user } = useAuth();
  const update = useUpdateName();
  const [name, setName] = useState(user?.user_metadata.full_name ?? '');
  const [done, setDone] = useState<string | null>(null);
  if (!user) return null;
  return (
    <section className="mb-12">
      <h2 className={head}>Profile</h2>
      <form
        className="flex flex-wrap gap-3 items-end mb-5"
        onSubmit={(e) => {
          e.preventDefault();
          setDone(null);
          update.mutate(name.trim(), {
            onSuccess: (r) =>
              setDone(
                r.signin_updated
                  ? 'Saved. Your new name shows everywhere after you next sign in.'
                  : 'Saved in the apps. The sign-in page will show it once your administrator finishes the account setup.',
              ),
          });
        }}
      >
        <label className="flex flex-col gap-1 font-label text-[11px] text-ink-muted flex-1 min-w-[220px]">
          Display name
          <input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} className="border border-rule bg-paper px-3 py-2 font-editorial text-[15px] text-ink" />
        </label>
        <button type="submit" className={btn} disabled={!name.trim() || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save name'}
        </button>
      </form>
      {done && <p className={note + ' -mt-2 mb-4'}>{done}</p>}
      {update.error && <p className={errCls}>{msg(update.error)}</p>}
      <div className="flex flex-wrap gap-4 items-baseline">
        <div className="font-editorial text-[15px] text-ink-soft">
          Email: <span className="text-ink">{user.email}</span>
        </div>
        <button type="button" className="font-label text-[11px] uppercase tracking-[0.15em] text-link hover:text-accent border-b border-accent-soft pb-0.5" onClick={() => startPasswordChange(user.email)}>
          Change password
        </button>
      </div>
      <p className={note + ' mt-2'}>
        Change password opens the sign-in page for your account: choose <strong className="not-italic">Forgot password?</strong>{' '}
        and you'll get a code by email to set a new one. If you sign in with Google or a one-time code, there's no password to change.
      </p>
    </section>
  );
}

export function DataSection() {
  const [busy, setBusy] = useState<'' | 'export' | 'delete'>('');
  const [err, setErr] = useState<string | null>(null);
  const [step, setStep] = useState(false);
  const [typed, setTyped] = useState('');

  async function exportNow() {
    setErr(null);
    setBusy('export');
    try {
      await downloadMyData();
    } catch (e) {
      setErr(msg(e));
    } finally {
      setBusy('');
    }
  }
  async function deleteNow() {
    setErr(null);
    setBusy('delete');
    try {
      await deleteMyAccount();
      sessionStorage.clear();
      await signOutEverywhere();
    } catch (e) {
      setErr(msg(e));
      setBusy('');
    }
  }

  return (
    <section className="mb-12">
      <h2 className={head}>Your data</h2>
      <div className="flex flex-wrap gap-4 items-center mb-2">
        <button type="button" className={btn} onClick={exportNow} disabled={!!busy}>
          {busy === 'export' ? 'Preparing…' : 'Download my data'}
        </button>
        <span className={note}>A JSON file of everything we hold about you: maps, assessment results, teams, checklists, invitations, the apps on your profile and your newsletter sign-up.</span>
      </div>

      {!step ? (
        <button type="button" onClick={() => { setStep(true); setTyped(''); setErr(null); }} className="mt-6 font-label text-[11px] uppercase tracking-[0.15em] text-[#a02525] border-b border-[#a02525] pb-0.5">
          Delete my account
        </button>
      ) : (
        <div className="mt-6 border border-[#a02525] p-4 bg-paper">
          <p className="font-editorial text-[15px] text-ink mb-2">
            This permanently deletes your account and everything in it: your maps (and their shares), assessment results,
            your team memberships, and teams where you're the only member. It can't be undone, so download your
            data first if you want a copy.
          </p>
          <p className={note + ' mb-3'}>If you own a team other people use, make someone else its owner first.</p>
          <label className="flex flex-col gap-1 font-label text-[11px] text-ink-muted max-w-xs">
            Type DELETE to confirm
            <input value={typed} onChange={(e) => setTyped(e.target.value)} className="border border-rule bg-paper px-3 py-2 font-editorial text-[15px] text-ink" />
          </label>
          <div className="flex gap-5 mt-4 items-baseline">
            <button type="button" onClick={deleteNow} disabled={typed !== 'DELETE' || !!busy} className="py-2 px-4 bg-[#a02525] text-white text-sm font-label disabled:opacity-40">
              {busy === 'delete' ? 'Deleting…' : 'Delete my account'}
            </button>
            <button type="button" className="font-label text-[11px] uppercase tracking-[0.15em] text-link border-b border-accent-soft pb-0.5" onClick={() => setStep(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {err && <p className={errCls}>{err}</p>}
    </section>
  );
}
